import { forwardRef, type ReactElement, type Ref } from 'react';

import {
  type DataTestId,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';

//#region Typings
/**
 * @public
 */
export type DataTableV2TableActionsProps = WithChildren &
  StylingProps &
  DataTestId;
//#endregion Typings

/**
 * A helper function to check whether the child component is the TableActions component
 * @internal
 */
export function _isTableActions(
  child: ReactElement | null,
): child is ReactElement<DataTableV2TableActionsProps> {
  return child?.type === DataTableV2TableActions;
}

/**
 * TableActions slot component for general actions that affect the entire table. Positoned above the header.
 * @public
 */
export const DataTableV2TableActions = forwardRef(
  (props: DataTableV2TableActionsProps, forwardedRef: Ref<HTMLDivElement>) => {
    const {
      children,
      'data-testid': dataTestId,
      className: consumerClassName,
      style: consumerStyle,
      ...remainingProps
    } = props;

    return (
      <Flex
        ref={forwardedRef}
        justifyContent="flex-start"
        alignItems="flex-end"
        flexWrap="wrap"
        gap={4}
        data-testid={dataTestId}
        className={consumerClassName}
        style={consumerStyle}
        {...remainingProps}
      >
        {children}
      </Flex>
    );
  },
);

(
  DataTableV2TableActions as typeof DataTableV2TableActions & {
    displayName: string;
  }
).displayName = 'DataTableV2.TableActions';
